import React from 'react';
import { ShieldAlert } from 'lucide-react';

/**
 * Global Error Boundary — catches unhandled render errors.
 * Displays a friendly fallback UI with recovery options.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Best-effort: report to backend if endpoint exists
    if (process.env.NODE_ENV === 'production') {
      const API = process.env.REACT_APP_API_URL;
      if (API) {
        fetch(`${API}/client-errors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: error?.message,
            stack: error?.stack?.slice(0, 500),
            component: errorInfo?.componentStack?.slice(0, 300),
            url: window.location.href,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {});
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-soem-bg flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
            <ShieldAlert size={28} className="text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-soem-navy mb-2">
            Algo salió mal
          </h1>
          <p className="text-slate-500 text-sm mb-6">
            Ha ocurrido un error inesperado. Puedes volver al inicio para continuar.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReset}
              className="px-5 py-2.5 bg-soem-primary text-white text-sm font-semibold rounded-sm hover:bg-soem-primary-hover transition-colors"
              data-testid="error-boundary-home-btn"
            >
              Volver al inicio
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 border border-slate-300 text-sm font-semibold text-soem-navy rounded-sm hover:bg-slate-50 transition-colors"
              data-testid="error-boundary-reload-btn"
            >
              Recargar página
            </button>
          </div>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <details className="mt-6 text-left bg-slate-100 rounded-sm p-3">
              <summary className="text-xs font-semibold text-slate-500 cursor-pointer">Detalle técnico</summary>
              <pre className="mt-2 text-[10px] text-red-700 whitespace-pre-wrap break-all">
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
